import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import BZAPIService from '../../services/BZAPIService';
import { formatScore, rangeFill, snapThreshold, thresholdSetting } from './predictWatchlistModel';
import './predictThreshold.css';

const errorText = error =>
  error?.message && error.message !== 'Request Failed' ? error.message : 'Could not save the threshold';

/**
 * The organization's Predict threshold on Settings, read from GET
 * /predict/threshold and saved through PUT /predict/threshold (STRY0039087).
 * A change needs the admin's approval: `change` and `reset` only set `draft`,
 * a pending value, and `apply` is the one call that saves it. `cancel` puts
 * the draft back on the saved value. A save the API refuses keeps the draft
 * pending, so it can be applied again or cancelled. `onApplied` runs after
 * each save, with the saved setting.
 */
export const usePredictThreshold = ({ onApplied } = {}) => {
  const [state, setState] = useState({ status: 'loading', setting: null });
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const saved = useRef(null);
  const onAppliedRef = useRef(onApplied);
  onAppliedRef.current = onApplied;

  const load = useCallback(() => {
    setState(current => ({ ...current, status: 'loading' }));
    BZAPIService.getPredictThreshold()
      .then(body => {
        const setting = thresholdSetting(body);
        saved.current = setting;
        setState({ status: 'ready', setting });
        setDraft(setting.threshold);
      })
      .catch(error => {
        console.error('Failed to load the Predict threshold:', error);
        setState({ status: 'failed', setting: null });
      });
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const change = useCallback(value => setDraft(value), []);

  const reset = useCallback(() => {
    if (saved.current) setDraft(saved.current.default);
  }, []);

  const cancel = useCallback(() => {
    if (saved.current) setDraft(saved.current.threshold);
  }, []);

  const apply = useCallback(async () => {
    if (!saved.current || draft === saved.current.threshold) return;
    setSaving(true);
    try {
      const setting = thresholdSetting(await BZAPIService.updatePredictThreshold(draft));
      saved.current = setting;
      setState({ status: 'ready', setting });
      setDraft(setting.threshold);
      toast.success(`Threshold set to ${formatScore(setting.threshold)} for your organization`);
      onAppliedRef.current?.(setting);
    } catch (error) {
      console.error('Failed to save the Predict threshold:', error);
      toast.error(errorText(error));
    } finally {
      setSaving(false);
    }
  }, [draft]);

  const pending = state.setting !== null && typeof draft === 'number' && draft !== state.setting.threshold;

  return {
    status: state.status,
    setting: state.setting,
    draft,
    pending,
    saving,
    change,
    reset,
    cancel,
    apply,
    reload: load
  };
};

/**
 * The threshold as a range input on the API's grid. Its track fills from the
 * value to the top end, the scores that reach the list.
 */
export const ThresholdSlider = ({ id, value, range, disabled, onChange, describedBy, block }) => (
  <input
    id={id}
    type="range"
    className={`pd-range${block ? ' pd-range--block' : ''}`}
    min={range.min}
    max={range.max}
    step={range.step}
    value={typeof value === 'number' ? value : range.default}
    disabled={disabled}
    aria-valuetext={formatScore(value)}
    aria-describedby={describedBy}
    style={{ '--fill': `${rangeFill(value, range)}%` }}
    onChange={event => onChange(snapThreshold(Number(event.target.value), range))}
  />
);
ThresholdSlider.propTypes = {
  id: PropTypes.string.isRequired,
  value: PropTypes.number,
  range: PropTypes.shape({
    default: PropTypes.number.isRequired,
    min: PropTypes.number.isRequired,
    max: PropTypes.number.isRequired,
    step: PropTypes.number.isRequired
  }).isRequired,
  disabled: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  describedBy: PropTypes.string,
  block: PropTypes.bool
};
